/**
 * `SCR-GLOSSARY` — P-THAI-01's desk. OWNER: web-frontend-engineer.
 *
 * LAW 31: *"then that crm will show the suit data to the manufacturer in both
 * english and in thai."* This is the room where that becomes true, one term at
 * a time, and the shape of it is an argument about what a translation IS.
 *
 * ── A SIGNATURE, NOT A FIELD ──────────────────────────────────────────────
 *
 * Nothing here is machine-translated and nothing ever will be: a work order is
 * a set of instructions for cutting cloth a man has paid four thousand dollars
 * for, and a plausible-looking guess in a language the reader trusts is worse
 * than English he has to ask about. So a term goes into Thai only when a named
 * person who reads both READS THE ENGLISH OF THE DAY and signs it, and the
 * screen says so where the person is about to do it: *"your name goes on it, on
 * every sheet it prints."*
 *
 * Three consequences, all visible rather than implied:
 *
 *   · **Append only.** A better translation is a NEW signature. The old one is
 *     not a mistake to be erased — it is what is on the work orders already
 *     printed and already cut from, and a record that can be edited cannot
 *     answer "what did the cutter actually read".
 *   · **Signed against the English of the day.** When the English moves under a
 *     term the server marks it STALE and work orders print English again. This
 *     screen shows that as its own state with its own sentence, because a term
 *     that silently reverted would be a term nobody re-signs.
 *   · **The queue is the default view.** `?status=unsigned` is what a bilingual
 *     reader came here to do; the other filters are for looking.
 *
 * The four counts and the two markers all come from the server. This screen
 * derives no status of its own.
 */

import { useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import { dateInWords } from '@pt/view';
import { api } from '../../lib/api.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import type { GlossaryTerm } from '../../lib/contracts.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { Button, FactLine, RovingRadioGroup, Statement, TextInput } from '../../components/primitives.tsx';
import { RefusalStatement } from '../../components/backoffice.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../../components/states.tsx';

const FILTERS = ['unsigned', 'signed', 'stale', 'all'] as const;

export function GlossaryScreen() {
  /* The queue first: what a bilingual reader came here to do. */
  const [filter, setFilter] = useState<string>('unsigned');
  const glossary = useResource(
    () => api.glossary(filter === 'all' ? {} : { status: filter }),
    [filter],
  );

  const terms = glossary.data?.terms ?? [];

  return (
    <AppShell title={t('gloss.heading')}>
      <Statement>{t('gloss.lede')}</Statement>
      {/* The server's own sentence about what this list is. */}
      {glossary.data === null ? null : <FactLine>{glossary.data.statement}</FactLine>}

      <fieldset className="pt-filter">
        <legend className="pt-filter__legend">{t('gloss.heading')}</legend>
        <RovingRadioGroup
          ariaLabel={t('gloss.heading')}
          values={FILTERS}
          chosen={filter}
          labelOf={(value) => tDynamic(`gloss.filter.${value}`)}
          onChoose={setFilter}
        />
      </fieldset>

      {glossary.phase === 'skeleton' || glossary.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="ledger" slow={glossary.slow} label={t('gloss.heading')} />
      ) : null}
      {glossary.phase === 'offline' ? <OfflineStatement /> : null}
      {glossary.phase === 'error' ? <FetchError onRetry={glossary.reload} /> : null}

      {glossary.phase !== 'ready' || glossary.data === null ? null : terms.length === 0 ? (
        <Statement>{t('gloss.empty')}</Statement>
      ) : (
        <>
          {/* The whole glossary, from the server that holds it — never the rows
              on this page, which would say "0 of 40" while looking at the
              unsigned queue. */}
          <FactLine>
            {t('gloss.count', {
              signedCount: String(glossary.data.signed),
              totalCount: String(glossary.data.total),
            })}
          </FactLine>
          <ul>
            {terms.map((term) => (
              <TermRow key={term.termKey} term={term} onSigned={glossary.reload} />
            ))}
          </ul>
          <FactLine>{t('gloss.sign.appendOnly')}</FactLine>
        </>
      )}
    </AppShell>
  );
}

function TermRow({ term, onSigned }: { term: GlossaryTerm; onSigned: () => void }) {
  const submission = useSubmission();
  const [open, setOpen] = useState(false);
  const [th, setTh] = useState('');
  const [note, setNote] = useState('');

  const sign = () => {
    if (th.trim() === '') return;
    void submission
      .run(() =>
        api.signGlossaryTerm(term.termKey, {
          th: th.trim(),
          ...(note.trim() === '' ? {} : { note: note.trim() }),
        }),
      )
      .then((result) => {
        if (result === null) return;
        setOpen(false);
        setTh('');
        setNote('');
        onSigned();
      });
  };

  return (
    <li className="pt-row">
      <FactLine label={t('gloss.term.en')} emphasis>
        {term.en}
      </FactLine>

      {/* WHAT IS ACTUALLY TRUE OF THIS TERM, in three mutually exclusive states.
          `stale` is not a quieter kind of `signed`: the Thai below it is real,
          a person wrote it, and it is NOT what work orders are printing. */}
      {term.status === 'signed' && term.th !== null ? (
        <>
          <FactLine label={t('gloss.term.th')}>
            <span lang="th">{term.th}</span>
          </FactLine>
          {term.signedByName === null || term.signedAt === null ? null : (
            <FactLine>
              {t('gloss.term.signed', {
                authorName: term.signedByName,
                at: dateInWords(term.signedAt),
              })}
            </FactLine>
          )}
        </>
      ) : term.status === 'stale' ? (
        <>
          {term.th === null ? null : (
            <FactLine label={t('gloss.term.th')}>
              <span lang="th">{term.th}</span>
            </FactLine>
          )}
          <FactLine>{t('gloss.term.stale')}</FactLine>
        </>
      ) : (
        <FactLine>{t('gloss.term.unsigned')}</FactLine>
      )}

      {open ? (
        <div className="pt-confirmstep">
          <TextInput
            id={`pt-gloss-${term.termKey}`}
            label={t('gloss.sign.label')}
            hint={t('gloss.sign.help')}
            value={th}
            onChange={setTh}
            multiline
            autoComplete="off"
          />
          <TextInput
            id={`pt-gloss-note-${term.termKey}`}
            label={t('gloss.sign.note.label')}
            value={note}
            onChange={setNote}
            autoComplete="off"
          />
          {submission.rejection === null ? null : (
            <RefusalStatement rejection={submission.rejection} />
          )}
          {submission.offline ? <OfflineStatement /> : null}
          <div className="pt-actions">
            <Button onClick={sign} busy={submission.busy}>
              {t('gloss.sign.submit')}
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('global.action.back')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="pt-actions">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            {term.status === 'unsigned' ? t('gloss.sign.submit') : t('gloss.sign.again')}
          </Button>
        </div>
      )}
    </li>
  );
}
